import { useTranslation } from "react-i18next";
import i18n from "@/lib/i18n";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import type { InviteEntryData } from "./InviteEntry";
import {
  useInviteSubmission,
  type SubmissionResult,
} from "./use-invite-submission";
import { useProductEvents } from "@/hooks/use-product-events";

interface InviteConfirmationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  validEntries: InviteEntryData[];
  allEntries: InviteEntryData[];
  onComplete: (result: SubmissionResult) => void;
  entityLabel?: string; // e.g., "Contractor" or "Team Member"
  idLabel?: string; // e.g., "Contractor ID" or "Team Member ID"
  showIdField?: boolean; // Whether to show the ID column (default: true)
}

export function InviteConfirmationDialog({
  open,
  onOpenChange,
  validEntries,
  allEntries,
  onComplete,
  entityLabel = i18n.t("components.invites.inviteConfirmationDialog.contractor"),
  idLabel = i18n.t("components.invites.inviteConfirmationDialog.contractorId"),
  showIdField = true,
}: InviteConfirmationDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { capture } = useProductEvents({
    component: "InviteConfirmationDialog",
  });
  const {
    isSubmitting,
    entryStatuses,
    entryErrorMessages,
    submitInvites,
    resetStatuses,
  } = useInviteSubmission();
  const [hasSubmitted, setHasSubmitted] = useState(false);

  // Create entry index map
  const entryIndexMap = new Map<InviteEntryData, number>();
  allEntries.forEach((entry, index) => {
    entryIndexMap.set(entry, index);
  });

  const hasErrors = Object.values(entryStatuses).some((s) => s === "error");

  const handleSubmit = async () => {
    // Capture confirm_send_invites event
    capture({
      event: "confirm_send_invites",
      context: {
        inviteCount: validEntries.length,
      },
    });

    setHasSubmitted(true);
    const result = await submitInvites(validEntries, entryIndexMap);

    const allFailed =
      result.successful.length === 0 && result.failed.length > 0;
    const someFailed = result.successful.length > 0 && result.failed.length > 0;

    if (allFailed) {
      // All emails failed - show error toast, keep dialog open
      toast({
        title: t("components.invites.inviteConfirmationDialog.errorSendingInvitations"),
        description:
          t("components.invites.inviteConfirmationDialog.weEncounteredAnIssue"),
        variant: "destructive",
        duration: Infinity,
      });
      return;
    }

    if (someFailed) {
      // Some succeeded, some failed - show toast with failed emails
      const failedEmails = result.failed.map((f) => f.entry.email).join(", ");
      toast({
        title: t("components.invites.inviteConfirmationDialog.someInvitationsFailedTo"),
        description: `The following emails failed: ${failedEmails}. We are investigating and will retry automatically.`,
        variant: "destructive",
        duration: Infinity,
      });
    }

    onComplete(result);
    onOpenChange(false);
  };

  // Reset when dialog closes
  useEffect(() => {
    if (!open) {
      resetStatuses();
      setHasSubmitted(false);
    }
  }, [open, resetStatuses]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("components.invites.inviteConfirmationDialog.confirmSubmission")}</DialogTitle>
          <DialogDescription>
            {t("components.invites.inviteConfirmationDialog.pleaseReviewThe")} {entityLabel.toLowerCase()} {t("components.invites.inviteConfirmationDialog.informationBeforeSubmittingYou")} {validEntries.length}{" "}
            {entityLabel.toLowerCase()}
            {validEntries.length > 1 ? "s" : ""}.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4">
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted">
                <tr>
                  <th className="px-4 py-3 text-left font-medium w-12">
                    {t("components.invites.inviteConfirmationDialog.status")}
                  </th>
                  <th className="px-4 py-3 text-left font-medium">{t("components.invites.inviteConfirmationDialog.name")}</th>
                  <th className="px-4 py-3 text-left font-medium">{t("components.invites.inviteConfirmationDialog.email")}</th>
                  {showIdField && (
                    <th className="px-4 py-3 text-left font-medium">
                      {idLabel}
                    </th>
                  )}
                  <th className="px-4 py-3 text-left font-medium">{t("components.invites.inviteConfirmationDialog.phone")}</th>
                </tr>
              </thead>
              <tbody>
                {validEntries.map((entry, index) => {
                  const entryIndex = allEntries.findIndex((e) => e === entry);
                  const status =
                    entryIndex !== -1
                      ? entryStatuses[entryIndex] || "pending"
                      : "pending";

                  return (
                    <tr key={index} className="border-t">
                      <td className="px-4 py-3">
                        {status === "success" && (
                          <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-500" />
                        )}
                        {status === "error" && (
                          <XCircle className="h-5 w-5 text-red-600 dark:text-red-500" />
                        )}
                        {status === "sending" && (
                          <Loader2 className="h-5 w-5 text-primary animate-spin" />
                        )}
                        {status === "pending" && (
                          <div className="h-5 w-5 rounded-full border-2 border-muted-foreground/30" />
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {entry.firstName} {entry.lastName}
                      </td>
                      <td className="px-4 py-3">{entry.email}</td>
                      {showIdField && (
                        <td className="px-4 py-3">{entry.customerId}</td>
                      )}
                      <td className="px-4 py-3">{entry.phone || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {Object.keys(entryErrorMessages).length > 0 && (
            <div className="mt-4 space-y-2">
              {Object.entries(entryErrorMessages).map(([index, error]) => {
                const entryIndex = parseInt(index);
                const entry = allEntries[entryIndex];
                if (!entry) return null;

                return (
                  <div
                    key={index}
                    className="text-sm text-destructive bg-destructive/10 p-2 rounded"
                  >
                    <strong>
                      {entry.firstName} {entry.lastName}:
                    </strong>{" "}
                    {error}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            {isSubmitting ? t("common.close") : t("common.cancel")}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || (hasSubmitted && hasErrors)}
          >
            {isSubmitting ? t("components.invites.inviteConfirmationDialog.sendingInvites") : t("components.invites.inviteConfirmationDialog.confirmSubmit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
